import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Sparkles, ShieldCheck, Zap, Wallet, Flame, Car,
} from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { fetchPopularSearches, fetchPublicStats } from "../lib/api";
import { HOME } from "../constants/testIds";
import { CATEGORIES } from "../lib/categories";
import { BasedOnYou } from "../components/home/BasedOnYou";
import { TodayPicks } from "../components/home/TodayPicks";
import { TrendingQuestions } from "../components/home/TrendingQuestions";
import { CategoryWinners } from "../components/home/CategoryWinners";
import { HiddenGems } from "../components/home/HiddenGems";
import { AvoidSection } from "../components/home/AvoidSection";

// Fallback chips shown while real popular-search data is sparse — only
// physical Amazon-UK products that the AI can confidently recommend.
const FALLBACK_EXAMPLES = [
  "Best Bluetooth speaker under £100",
  "Best wireless earbuds under £150",
  "Best noise-cancelling headphones",
  "Best soundbar under £300",
  "Best OLED TV under £1500",
  "Best 4K monitor for home office",
  "Best laptop for photo editing under £1000",
  "Best iPad for students",
  "Best mesh wifi router for a 4-bed house",
  "Best air fryer for a family of 4",
  "Best coffee machine for hard water areas",
  "Best stand mixer for bread baking",
  "Best cordless vacuum under £400",
  "Best robot vacuum for pet hair",
  "Best cordless drill under £150",
  "Best office chair for back pain",
  "Best running shoes for flat feet",
  "Best smartwatch for tracking workouts",
  "Best electric toothbrush",
  "Best gaming mouse under £80",
  "Best action camera for cycling",
];

// Fisher–Yates shuffle — returns a NEW shuffled copy so the chip strip is
// fresh on every page load without flicker (memoised at mount).
const shuffle = (arr) => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const TRUST = [
  { icon: ShieldCheck, label: "Honest verdict" },
  { icon: Zap,         label: "Decision in seconds" },
  { icon: Wallet,      label: "Value-led, not hype-led" },
];

const formatNumber = (n) => {
  if (n == null) return "—";
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k+`;
  return `${n}+`;
};

const TrustMetric = ({ label, value, testId }) => (
  <div data-testid={testId} className="flex flex-col items-center px-4">
    <div className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-emerald-500">{value}</div>
    <div className="text-[11px] text-muted-foreground mt-0.5">{label}</div>
  </div>
);

export default function Home() {
  const [q, setQ] = useState("");
  const [popular, setPopular] = useState([]);
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    fetchPopularSearches().then((d) => setPopular(d || [])).catch(() => setPopular([]));
    fetchPublicStats().then(setStats).catch(() => setStats(null));
  }, []);

  const go = (text) => {
    const message = (text || q).trim();
    if (!message) return;
    navigate("/chat", { state: { initial: message } });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    go();
  };

  // Memoise a shuffled fallback so chip order is stable per page load.
  const fallbackShuffled = useState(() => shuffle(FALLBACK_EXAMPLES).slice(0, 8))[0];

  const popularItems =
    popular.length >= 3
      ? popular.slice(0, 8).map((p) => p.query)
      : fallbackShuffled;

  const showTrustNumbers = stats?.display;

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-hidden">
      {/* Ambient background layers */}
      <div className="absolute inset-0 grid-pattern opacity-50 pointer-events-none" />
      <div className="absolute inset-x-0 top-0 h-[640px] radial-fade pointer-events-none animate-ambient" />
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-emerald-500/[0.06] blur-3xl pointer-events-none" />
      <div className="absolute top-40 -left-32 w-80 h-80 rounded-full bg-emerald-500/[0.05] blur-3xl pointer-events-none" />

      <div className="relative z-10">
        <Header />
      </div>

      <main className="relative z-10 flex-1 flex flex-col items-center px-5 pt-8 sm:pt-14 pb-20">
        <div className="w-full max-w-5xl">
          {/* Hero (centred, narrower than the discovery rows below) */}
          <div className="max-w-3xl mx-auto">
            <div className="flex justify-center mb-7 animate-float-up">
              <div
                data-testid="value-pill"
                className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-medium"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Independent AI Buying Advice
              </div>
            </div>

            <h1 className="text-center font-display font-bold text-5xl sm:text-6xl md:text-7xl tracking-tighter text-foreground mb-5 leading-[1.02] animate-float-up stagger-1">
              Before you buy it, <span className="text-emerald-500">ask us</span>.
            </h1>
            <p
              data-testid="value-subtitle"
              className="text-center text-base sm:text-lg md:text-xl text-muted-foreground mb-10 sm:mb-12 max-w-xl mx-auto leading-relaxed animate-float-up stagger-2"
            >
              Independent AI buying advice, product verdicts, alternatives and value checks for Amazon UK shoppers.
            </p>

            {/* Search */}
            <form onSubmit={onSubmit} className="relative animate-float-up stagger-3">
              <div className="relative group">
                <div className="absolute -inset-0.5 rounded-2xl bg-gradient-to-r from-emerald-500/0 via-emerald-500/20 to-emerald-500/0 opacity-0 group-focus-within:opacity-100 blur-md transition-opacity duration-500 pointer-events-none" />
                <input
                  ref={inputRef}
                  data-testid={HOME.searchInput}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="What are you thinking of buying?"
                  className="relative w-full text-base sm:text-lg bg-card border border-border rounded-2xl py-5 sm:py-6 pl-6 pr-16 text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-emerald-500/60 focus:glow-emerald transition-all"
                />
                <button
                  type="submit"
                  data-testid={HOME.submitBtn}
                  disabled={!q.trim()}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 flex items-center justify-center transition disabled:opacity-40 disabled:cursor-not-allowed glow-emerald-soft hover:scale-105 active:scale-95"
                  aria-label="Ask"
                >
                  <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
                </button>
              </div>
            </form>

            {/* Trust numbers */}
            {showTrustNumbers && (
              <div
                data-testid="trust-metrics"
                className="mt-7 flex flex-wrap items-center justify-center gap-x-2 gap-y-3 sm:gap-x-4 divide-x divide-border animate-float-up stagger-4"
              >
                <TrustMetric
                  testId="trust-recommendations"
                  value={formatNumber(stats.total_recommendations)}
                  label="Recommendations"
                />
                {stats.distinct_products > 0 && (
                  <TrustMetric
                    testId="trust-products"
                    value={formatNumber(stats.distinct_products)}
                    label="Products analysed"
                  />
                )}
                {stats.show_approval && stats.approval_percent != null ? (
                  <TrustMetric
                    testId="trust-approval"
                    value={`${stats.approval_percent}%`}
                    label="User approval"
                  />
                ) : (
                  <div
                    data-testid="trust-uk-focused"
                    className="flex flex-col items-center px-4"
                  >
                    <div className="font-display font-bold text-xl sm:text-2xl tracking-tight text-emerald-500">
                      UK
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">Focused recommendations</div>
                  </div>
                )}
              </div>
            )}

            {/* Popular searches chips */}
            <section className="mt-10 sm:mt-12 animate-float-up stagger-4" data-testid="popular-section">
              <div className="flex items-center gap-2 mb-3">
                <Flame className="w-4 h-4 text-emerald-500" />
                <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                  {popular.length >= 3 ? "Popular this week" : "Start with these"}
                </span>
              </div>
              <div className="flex flex-wrap gap-2.5">
                {popularItems.map((ex, i) => (
                  <button
                    key={ex}
                    onClick={() => go(ex)}
                    data-testid={HOME.exampleChip(i)}
                    className="text-xs sm:text-sm px-4 py-2 rounded-full bg-card border border-border text-muted-foreground hover:text-foreground hover:border-emerald-500/40 hover:bg-emerald-500/5 transition-all"
                  >
                    {ex}
                  </button>
                ))}
              </div>
            </section>
          </div>

          {/* Cars cross-promo — featured new sister product. Native look:
              same bg-card + border, same emerald accent, same rounded-2xl
              radius as the search input. Not a full hero — a single-row
              feature card that sits between the primary search and the
              discovery rows. */}
          <a
            href="https://shouldibuyitcars.vercel.app/"
            data-testid="cars-promo"
            className="mt-10 sm:mt-12 block group animate-float-up stagger-4"
          >
            <div className="relative overflow-hidden rounded-2xl bg-card border border-border hover:border-emerald-500/40 transition-all p-5 sm:p-6">
              <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-emerald-500/[0.07] blur-3xl pointer-events-none" />
              <div className="relative flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                <div className="w-12 h-12 sm:w-14 sm:h-14 shrink-0 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center group-hover:bg-emerald-500/20 group-hover:border-emerald-500/40 transition">
                  <Car className="w-6 h-6 sm:w-7 sm:h-7 text-emerald-500 group-hover:scale-110 transition-transform" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-widest text-emerald-500 font-semibold mb-1.5">
                    New — Should I Buy It Cars
                  </div>
                  <div className="font-display font-bold text-lg sm:text-xl text-foreground leading-tight mb-1">
                    Looking for a car in the UK?
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Try our brand-new car search engine and compare live listings from multiple marketplaces in one place.
                  </p>
                </div>
                <div className="shrink-0 sm:self-center">
                  <span
                    data-testid="cars-promo-cta"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-sm font-semibold transition glow-emerald-soft group-hover:scale-[1.02] active:scale-95"
                  >
                    Search cars
                    <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                  </span>
                </div>
              </div>
            </div>
          </a>

          {/* DISCOVERY ROWS (wider than the hero) */}
          <BasedOnYou />
          <TodayPicks />
          <TrendingQuestions />
          <CategoryWinners />
          <HiddenGems />
          <AvoidSection />

          {/* Browse by category */}
          <section className="mt-12 sm:mt-16" data-testid="category-section">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-emerald-500" />
              <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                Browse by category
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
              {CATEGORIES.map(({ icon: Icon, label, slug }) => (
                <button
                  key={slug}
                  onClick={() => navigate(`/browse/${slug}`)}
                  data-testid={`category-${slug}`}
                  className="hover-lift flex flex-col items-center gap-2 py-4 px-2 rounded-xl bg-card border border-border hover:border-emerald-500/40 group"
                >
                  <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center group-hover:bg-emerald-500/20 group-hover:border-emerald-500/40 transition">
                    <Icon className="w-4 h-4 text-emerald-500 group-hover:scale-110 transition-transform" strokeWidth={2} />
                  </div>
                  <span className="text-[11px] sm:text-xs font-medium text-foreground/90">{label}</span>
                </button>
              ))}
            </div>
          </section>

          {/* Trust strip */}
          <div className="mt-12 sm:mt-16 flex flex-wrap items-center justify-center gap-6 sm:gap-10">
            {TRUST.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 text-muted-foreground text-sm">
                <Icon className="w-4 h-4 text-emerald-500" />
                {label}
              </div>
            ))}
          </div>
        </div>
      </main>

      <footer className="relative z-10">
        <Footer />
      </footer>
    </div>
  );
}
